import React, { type FC, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';

import { Step } from '../../types';
import { FeatureFlag } from 'app/api/services/site-configs/types';

import { Button, ButtonIntent, ButtonType } from 'app/design-system/components/Button';
import { Icon, IconSize } from 'app/design-system/components/Icon';
import { StepperContext } from 'app/design-system/components/Stepper/context';
import { useTranslation } from 'app/lang';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { TelegramSign } from 'app/widgets/TelegramSign';

import { ROUTE_PATH } from '../../../../lib/routing/constants';
import { FORWARD_BUTTON_CONTENT } from '../../constants';
import { RegContext } from '../../context';
import { useTelegramOptions } from '../../hooks/useTelegramMethods';
import { createAuthQuery } from '../../utils/auth-modals-query';
import { OrDivider } from '../OrDivider';

import dictionary from './SignModal.dictionary.json';

import styles from './SignModal.module.scss';

export const SignFooter: FC = () => {
  const isAllowTelegramSign = useFeatureFlag(FeatureFlag.ALLOW_TELEGRAM_SIGN);

  const { t } = useTranslation();

  const { regData } = useContext(RegContext);
  const {
    telegramProcessing,
    handleTelegramOnSuccess,
    handleTelegramOnFail,
    handleTelegramOnProcessing,
  } = useTelegramOptions();
  const { activeStep, backwardStep } = useContext(StepperContext);

  const { disabled, checked } = regData;

  const buttonContainerClassNames = classNames(styles.container, {
    [styles.initialContainer]: activeStep === Step.INITIAL,
  });

  const backwardButtonHandler = useCallback(() => backwardStep(), []);

  const backwardButtonText = t(dictionary.back);

  const forwardButtonText = checked
    ? (
      <Icon icon="check"
            size={IconSize.MEDIUM}
            className={styles.iconSuccess} />
    )
    : (
      activeStep === Step.FINISH ? '' : t(FORWARD_BUTTON_CONTENT[`STEP${activeStep}`])
    );

  if (activeStep === Step.FINISH) {
    return (
      <div className={buttonContainerClassNames}>
        <Button
          component={Link}
          to={`${ROUTE_PATH.DEPOSIT}?${createAuthQuery('close')}`}
          intent={ButtonIntent.CALL_TO_ACTION}
          block>
          {t(dictionary.depositLink)}
        </Button>
        <Button
          component={Link}
          to={ROUTE_PATH.ROOT}
          intent={ButtonIntent.DEFAULT}
          block>
          {t(dictionary.mainLink)}
        </Button>
      </div>
    );
  }

  return (
    <div className={buttonContainerClassNames}>
      <Button
        type={ButtonType.SUBMIT}
        intent={!checked ? ButtonIntent.CALL_TO_ACTION : ButtonIntent.SUCCESS}
        form={`registrationFormStep_${activeStep}`}
        loading={disabled && !checked}
        block
        data-test="registration-submit">
        {forwardButtonText}
      </Button>

      {
        isAllowTelegramSign && activeStep === Step.INITIAL && (
          <>
            <OrDivider />

            <TelegramSign
    className={styles.telegramSign}
    processing={telegramProcessing}
    onProcessing={handleTelegramOnProcessing}
    onFail={handleTelegramOnFail}
    onSuccess={handleTelegramOnSuccess} />
          </>
        )
      }

      { activeStep === Step.CODE && (
        <Button type={ButtonType.BUTTON}
                intent={ButtonIntent.DEFAULT}
                onClick={backwardButtonHandler}
                disabled={disabled}
                block
                className={styles.footerCancelButton}
                data-test="registration-cancel">
          {backwardButtonText}
        </Button>
      )}
    </div>
  );
};
